import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { cn } from '../../../lib/utils';
import { SpecraIcon } from '../ensigns/icon';
import { SpecraText } from '../ensigns/typography';
import { SpecraStatusPill } from './status-pill';
// A click on an action inside a <summary> must not toggle its <details>.
// A link keeps its own navigation (and never toggles the summary anyway).
const isActionClick = (event)=>event.target instanceof Element && Boolean(event.target.closest('[data-record-group-actions]')) && !event.target.closest('a[href]');
/** Expand/collapse heading for grouped deliverables and hierarchical records. */ export function SpecraRecordGroupHeader({ actions, as = 'button', className, count, controls, description, open = false, status, title, type = 'button', variant = 'card', ...props }) {
    const hairline = variant === 'hairline';
    const summary = as === 'summary';
    const wrapped = hairline || Boolean(actions) || summary;
    const chevron = /*#__PURE__*/ _jsx(SpecraIcon, {
        className: cn('transition-transform', summary ? 'group-open/record:rotate-90' : open && 'rotate-90'),
        children: "›"
    });
    const body = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            chevron,
            hairline ? /*#__PURE__*/ _jsxs("span", {
                className: "flex min-w-0 flex-1 items-baseline gap-[var(--space-2)]",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "min-w-0 truncate font-sans text-[length:var(--fs-ui-eyebrow)] font-medium uppercase leading-[var(--lh-tight)] tracking-[var(--ls-ui-group)] text-muted-foreground",
                        children: title
                    }),
                    description ? /*#__PURE__*/ _jsx(SpecraText, {
                        className: "min-w-0 truncate",
                        size: "micro",
                        tone: "muted",
                        children: description
                    }) : null
                ]
            }) : /*#__PURE__*/ _jsxs("span", {
                className: "min-w-0 flex-1",
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        className: "block truncate font-medium",
                        size: "caption",
                        children: title
                    }),
                    description ? /*#__PURE__*/ _jsx(SpecraText, {
                        className: "block truncate",
                        size: "micro",
                        tone: "muted",
                        children: description
                    }) : null
                ]
            }),
            count !== undefined ? /*#__PURE__*/ _jsx(SpecraStatusPill, {
                children: count
            }) : null,
            status
        ]
    });
    const frameClass = hairline ? 'flex w-full items-center gap-[var(--space-3)] border-b border-border pb-[var(--space-1-5)]' : 'flex w-full items-center gap-3 rounded-md bg-muted/50 px-3 py-2.5 hover:bg-muted';
    if (!wrapped) {
        return /*#__PURE__*/ _jsx("button", {
            "aria-controls": controls,
            "aria-expanded": open,
            className: cn(frameClass, 'text-left', className),
            type: type,
            ...props,
            children: body
        });
    }
    const toggleClass = cn('flex min-w-0 flex-1 items-center text-left', hairline ? 'gap-[var(--space-2)] hover:text-foreground' : 'gap-3');
    const actionSlot = actions ? /*#__PURE__*/ _jsx("div", {
        className: "ml-auto flex shrink-0 items-center gap-[var(--space-2)]",
        "data-record-group-actions": "",
        children: actions
    }) : null;
    if (summary) {
        const { onClick, ...summaryProps } = props;
        return(// biome-ignore lint/a11y/noStaticElementInteractions: <summary> is the native details toggle
        /*#__PURE__*/ _jsxs("summary", {
            ...summaryProps,
            className: cn(frameClass, 'cursor-pointer list-none [&::-webkit-details-marker]:hidden', className),
            "data-variant": variant,
            onClick: (event)=>{
                if (isActionClick(event)) event.preventDefault();
                onClick?.(event);
            },
            children: [
                /*#__PURE__*/ _jsx("span", {
                    className: toggleClass,
                    children: body
                }),
                actionSlot
            ]
        }));
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: cn(frameClass, className),
        "data-variant": variant,
        children: [
            /*#__PURE__*/ _jsx("button", {
                "aria-controls": controls,
                "aria-expanded": open,
                className: toggleClass,
                type: type,
                ...props,
                children: body
            }),
            actionSlot
        ]
    });
}
